import { useRef, type ReactNode } from "react";
import { cn } from "../../lib/cn";
import { STICKY_TITLE_VAR, useStickyOffset } from "../../lib/useStickyOffset";

export interface PageHeaderProps {
  title: string;
  description?: string;
  /** Primary page action, right-aligned on wide screens and wrapped below the title on narrow. */
  actions?: ReactNode;
  /**
   * Pins the header to the top of the content area so it stays visible while a long table
   * scrolls beneath it. Offset by the app header (h-16) rather than 0, or it would slide
   * underneath the translucent bar and the title would show through it.
   */
  sticky?: boolean;
  className?: string;
}

/**
 * One header for every page.
 *
 * Each page previously hand-rolled this, and they had drifted: different title sizes, different
 * title→description gaps, and different spacing before the content below. Because the rhythm
 * lives here, "the gap under a page title" is now a single decision rather than eight.
 */
export function PageHeader({ title, description, actions, sticky, className }: PageHeaderProps) {
  const ref = useRef<HTMLDivElement>(null);
  useStickyOffset(ref, STICKY_TITLE_VAR, Boolean(sticky));

  return (
    <div
      ref={ref}
      className={cn(
        "flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6",
        sticky && [
          // top-16 clears the app header, which is exactly h-16 — the two abut rather than
          // overlap. z-20 sits above the table's sticky column (z-[1]) and below the app
          // header (z-30).
          "sticky top-16 z-20 pb-4 pt-4",
          // Fully opaque, not bg-bg/85. Measured, the alpha alone still cleared AA (18.55:1 for
          // the title over a row) — the unreadability came from the table header painting over
          // this element, fixed by the z-index above. Opaque is still correct: it makes
          // legibility independent of whatever scrolls underneath instead of a coincidence of
          // which two colours happen to overlap.
          "bg-bg",
          // With an opaque surface the content needs a boundary, or rows appear to vanish at an
          // invisible line as they scroll under.
          //
          // No negative-margin bleed into the page gutters: nothing ever scrolls there (the
          // content column is already inset by main padding), and on Account — which is
          // max-w-3xl inside the wider container — bleeding left the header 32px wider than
          // the cards under it.
          "border-b border-border",
        ],
        className,
      )}
    >
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold tracking-tight text-text sm:text-[28px] sm:leading-9">
          {title}
        </h1>
        {description && (
          // max-w keeps the line length readable on a 1920px screen, where a full-width
          // paragraph becomes a single stretched line that is genuinely harder to read.
          <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-text-muted">{description}</p>
        )}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}
